import * as ModelEvent from "@node-elion/syncron";
import { uniq } from "lodash";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import { createObjectLanguageNames } from "../../assets/languages/langs";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { SortingOrder } from "../../types/SortingOrder";
import SubscriptionBase from "../../types/Subscription";
import { Base, Language, TaxiService } from "..";

import type { ExecutorRatingForm } from "../../types/executorRating";

const logger = createLogger({ name: "ExecutorRating" });

function taxiServiceIds(data: { taxiServices?: { id: number }[] }): number[] {
	try {
		const taxiServices = data?.taxiServices ?? [];
		if (!taxiServices.length) return [];
		return uniq<number>(
			taxiServices.map((item) => item?.id).filter(Boolean),
		);
	} catch {
		return [];
	}
}

class ExecutorRating extends Base {
	static fromResponse(
		data: Record<string, unknown>,
		lang: Language = "uk",
	): ExecutorRating.Model {
		const ids = taxiServiceIds(data as { taxiServices?: { id: number }[] });
		const executors = (data?.executors as { id?: number }[]) ?? [];
		const executorIds = executors
			.map((e) => e?.id)
			.filter((id): id is number => typeof id === "number");
		const config = (data?.ratingRulesConfiguration ?? {}) as {
			ratingDeduction?: ExecutorRatingForm["ratingDeduction"];
			ratingAccrual?: ExecutorRatingForm["ratingAccrual"];
		};
		const ratingDeduction = config.ratingDeduction ?? {};
		const ratingAccrual = config.ratingAccrual ?? {};
		return {
			id: (data?.id as number) ?? 0,
			active: (data?.active as boolean) ?? true,
			isDefault: (data?.isDefault as boolean) ?? false,
			root: (data?.root as boolean) ?? false,
			name:
				(data?.name as Record<string, string>)?.[lang] ??
				(data?.name as Record<string, string>)?.ru ??
				"",
			taxiServiceIds: (data?.taxiServiceIds as number[]) ?? ids,
			taxiServices: (data?.taxiServices as TaxiService.Model[]) ?? [],
			ratingDeduction,
			ratingAccrual,
			defaultRatingPoints: (data?.defaultRatingPoints as number) ?? 0,
			createdAt: (data?.createdAt as string) ?? "",
			updatedAt: (data?.updatedAt as string) ?? "",
			deletedAt: (data?.deletedAt as string) ?? null,
			executorIds,
		};
	}

	static toRequest(
		model: ExecutorRating.Model.New | ExecutorRating.Model.Modified,
	): Record<string, unknown> {
		const result: Record<string, unknown> = {
			name: createObjectLanguageNames(model.name),
			active: model.active,
			isDefault: model.isDefault,
			taxiServiceIds: model.taxiServiceIds,
			ratingDeduction: model.ratingDeduction ?? {},
			ratingAccrual: model.ratingAccrual ?? {},
			defaultRatingPoints: model.defaultRatingPoints ?? 0,
		};
		if (
			"executorIds" in model &&
			Array.isArray((model as ExecutorRating.Model.Modified).executorIds)
		) {
			result.executorIds = (
				model as ExecutorRating.Model.Modified
			).executorIds;
		}
		const modified = model as ExecutorRating.Model.Modified;
		if (modified.applyDefaultRatingOnSave === true) {
			result.applyDefaultRatingOnSave = true;
		}
		return result;
	}

	public static async getById(id: number, lang?: Language) {
		const data = await this.request((prpc) =>
			prpc.theirsModel.executorRating.getById(id),
		);
		if (!data) return null;
		return ExecutorRating.fromResponse(
			data as Record<string, unknown>,
			lang,
		);
	}

	public static async store(object: ExecutorRating.Model.New, force = false) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorRating.create(
						ExecutorRating.toRequest(object) as Record<
							string,
							unknown
						>,
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("ExecutorRating store", { object, res, force });
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async update(
		object: ExecutorRating.Model.Modified,
		force = false,
	) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.executorRating.update(
						object.id,
						ExecutorRating.toRequest(object) as Record<
							string,
							unknown
						>,
						{ force },
					),
				{ silent: false, error: true },
			);
			logger.info("ExecutorRating update", {
				object,
				res,
				force,
			});
			return res;
		} catch (error) {
			return null;
		}
	}

	public static async destroy(id: number[] | number) {
		if (Array.isArray(id))
			await Promise.all(id.map((idItem) => this.destroyOne(idItem)));
		else await this.destroyOne(id);
	}

	public static async subscribe(
		options: ExecutorRating.SubscribeOptions,
		onUpdate: SubscriptionBase.OnUpdate<ExecutorRating.Model>,
	): Promise<SubscriptionBase<ExecutorRating.SubscribeOptions> | null> {
		const currentOptionsRef: { current: ExecutorRating.SubscribeOptions } =
			{
				current: options,
			};

		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				const opts = currentOptionsRef.current;
				logger.info("[ExecutorRating] subscribe", {
					options: opts,
					state,
				});
				onUpdate({
					...state,
					models: (state.models as Record<string, unknown>[]).map(
						(item) =>
							ExecutorRating.fromResponse(
								item as Record<string, unknown>,
								opts.lang,
							),
					),
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.executorRating.subscribe({
					params: ExecutorRating.optionsToRequest(options),
					ping: () => true,
					onEvent: (event: unknown) => {
						modelEventConstructor.onEvent(
							event as Parameters<
								ModelEvent.ModelEventConstructor["onEvent"]
							>[0],
						);
					},
					onError: (error: unknown) => {
						logger.error(error);
					},
				}),
			{ name: "ExecutorRating.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (opts: ExecutorRating.SubscribeOptions) => {
				currentOptionsRef.current = opts;
				return subscription.update(
					ExecutorRating.optionsToRequest(opts),
				);
			},
		} as SubscriptionBase<ExecutorRating.SubscribeOptions>;
	}

	private static async destroyOne(id: number) {
		this.request((prpc) => prpc.theirsModel.executorRating.delete(id));
	}

	private static optionsToRequest(options: ExecutorRating.SubscribeOptions) {
		return {
			query: options.query,
			offset: options.offset,
			limit: options.limit,
			lang: options.lang,
			taxiServiceIds: options.taxiServiceIds,
			order: options.order,
		};
	}
}

declare namespace ExecutorRating {
	export interface Model {
		id: number;
		active: boolean;
		isDefault: boolean;
		root: boolean;
		name: string;
		taxiServiceIds: number[];
		taxiServices?: TaxiService.Model[];
		ratingDeduction: ExecutorRatingForm["ratingDeduction"];
		ratingAccrual: ExecutorRatingForm["ratingAccrual"];
		defaultRatingPoints: number;
		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
		executorIds?: number[];
	}

	interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<ExecutorRating.Model>,
			"order"
		> {
		taxiServiceIds?: number[];
		lang?: Language;
		order?: Partial<Record<keyof Model, SortingOrder>>;
	}

	namespace Model {
		interface New {
			name: string;
			active: boolean;
			isDefault: boolean;
			taxiServiceIds: number[];
			ratingDeduction: ExecutorRatingForm["ratingDeduction"];
			ratingAccrual: ExecutorRatingForm["ratingAccrual"];
			defaultRatingPoints?: number;
			/** One-shot on create/update; not returned from API / not stored. */
			applyDefaultRatingOnSave?: boolean;
		}
		type Modified = Partial<New> & {
			id: number;
			executorIds?: number[];
		};
	}
}

export default ExecutorRating;
